import { makeImmutable } from "../core/makeImmutable";
import { UnknownElementError } from "./errors";
/*
 * Restaumatic Array wrapper including helper array operations. Minimal alternative to lodash/underscore libraries
 */
export class RArray {
    constructor(raw) {
        this._raw = [...raw];
        makeImmutable(this);
    }
    get raw() {
        // TODO: make it an readonly array
        return this._raw;
    }
    static empty() {
        return new RArray([]);
    }
    static singleton(element) {
        return new RArray([element]);
    }
    static range(size) {
        return new RArray([...Array(size).keys()]);
    }
    static repeat(element, count) {
        return new RArray(new Array(count.value).fill(element));
    }
    static eq(arrayA, arrayB) {
        if (arrayA.size !== arrayB.size) {
            return false;
        }
        for (let i = 0; i < arrayA.size; ++i) {
            if (!arrayA.get(i).eq(arrayB.get(i))) {
                return false;
            }
        }
        return true;
    }
    static unique(array) {
        return array.filtered((element, index) => { var _a; return ((_a = array.firstIndexWhere((other) => element.eq(other))) !== null && _a !== void 0 ? _a : index) === index; });
    }
    [Symbol.iterator]() {
        return this._raw.values();
    }
    get size() {
        return this._raw.length;
    }
    get isEmpty() {
        return this.size === 0;
    }
    map(mapping) {
        return new RArray(this._raw.map(mapping));
    }
    mapOptional(mapping) {
        const res = this._raw.map(mapping).filter((el) => el !== null);
        return new RArray(res);
    }
    forEach(action) {
        this._raw.forEach(action);
    }
    flatMap(mapping) {
        return new RArray(this._raw.flatMap((element) => mapping(element)._raw));
    }
    reduce(reducer, initialValue) {
        return this._raw.reduce(reducer, initialValue);
    }
    filtered(predicate) {
        return new RArray(this._raw.filter(predicate));
    }
    every(predicate) {
        return this._raw.every(predicate);
    }
    some(predicate) {
        return this._raw.some(predicate);
    }
    count(predicate) {
        return this._raw.reduce((count, element) => count + (predicate(element) ? 1 : 0), 0);
    }
    includes(element) {
        return this._raw.includes(element);
    }
    drop(n) {
        return new RArray(this._raw.slice(n));
    }
    sorted(comparator) {
        return new RArray([...this._raw].sort(comparator));
    }
    chunks(ofSize) {
        if (ofSize < 1) {
            throw new Error("Chunks size must be positive");
        }
        const res = [];
        for (let i = 0; i < this.size; i += ofSize) {
            const chunk = new RArray(this.raw.slice(i, i + ofSize));
            res.push(chunk);
        }
        return new RArray(res);
    }
    concatenated(other) {
        return new RArray([...this._raw, ...other]);
    }
    shallowClone() {
        return new RArray([...this._raw]);
    }
    firstIndexWhere(predicate) {
        const index = this._raw.findIndex(predicate);
        return index === -1 ? null : index;
    }
    find(index) {
        var _a;
        return (_a = this._raw[index]) !== null && _a !== void 0 ? _a : null;
    }
    findWhere(predicate) {
        var _a;
        return (_a = this._raw.find(predicate)) !== null && _a !== void 0 ? _a : null;
    }
    get(index) {
        const element = this.find(index);
        if (element === null) {
            throw new UnknownElementError();
        }
        return element;
    }
    get last() {
        if (this.isEmpty) {
            throw new Error("Unable to call last on empty array");
        }
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return this._raw[this.size - 1];
    }
    sliceAt(index) {
        const p1 = this._raw.slice(0, index);
        const p2 = this._raw.slice(index);
        return [new RArray(p1), new RArray(p2)];
    }
    take(count) {
        return new RArray(this._raw.slice(0, count));
    }
}
